"use client";
import React, { createContext, useContext, ReactNode } from "react";
import { useAppSelector } from "lib/redux/hooks";
import {
  selectHighlightEnabled,
  selectHighlightMode,
  selectHighlightSection,
  selectHighlightItemIndex,
  selectHighlightTexts,
  selectHighlightStyle, 
} from "lib/redux/highlightSlice";

interface HighlightContextValue {
  enabled: boolean;
  mode: "word" | "sentence" | "section" | null;
  texts: string[];
  // the  section that is currently targeted (e.g. 'experience')
  section: string | null;
  // index within that section (e.g. experience item index)
  itemIndex: number | null;
  // Dynamic style based on current mode
  style: {
    backgroundColor: string;
    textDecoration: string;
    textDecorationColor: string;
    textDecorationStyle: string;
    fontWeight: string;
    color?: string; 
  };
}

const HighlightContext = createContext<HighlightContextValue>({
  enabled: false,
  mode: null,
  texts: [],
  section: null,
  itemIndex: null,
  style: {
    backgroundColor: "transparent",
    textDecoration: "none",
    textDecorationColor: "transparent",
    textDecorationStyle: "solid",
    fontWeight: "normal",
    color: "inherit",
  },
});

/**
 * Provider that reads highlight state from Redux and provides it via Context
 * This allows iframe components to access highlight state without prop drilling
 * The style is dynamically selected based on the current highlight mode
 */
export const HighlightProvider = ({ children }: { children: ReactNode }) => {
  const enabled = useAppSelector(selectHighlightEnabled);
  const mode = useAppSelector(selectHighlightMode);
  const section = useAppSelector(selectHighlightSection);
  const itemIndex = useAppSelector(selectHighlightItemIndex);
  const texts = useAppSelector(selectHighlightTexts);
  const style = useAppSelector(selectHighlightStyle); // Now mode-aware

  const value: HighlightContextValue = {
    enabled,
    mode,
    texts: texts as string[],
    section: section || null,
    itemIndex: typeof itemIndex === 'number' ? itemIndex : null,
    style: {
      backgroundColor: style.backgroundColor || "transparent",
      textDecoration: style.textDecoration || "none",
      textDecorationColor: style.textDecorationColor || "transparent",
      textDecorationStyle: style.textDecorationStyle || "solid",
      fontWeight: style.fontWeight || "normal",
      color: style.color || "inherit",
    },
  };

  return (
    <HighlightContext.Provider value={value}>
      {children}
    </HighlightContext.Provider>
  );
};

/**
 * Hook to access highlight context in iframe components
 */
export const useHighlight = () => {
  return useContext(HighlightContext);
};

/**
 * Helper function to check if a text should be highlighted
 * supports composite checking (both sentence and word highlights)
 */
export const shouldHighlightText = (
  text: string,
  highlightTexts: string[],
  mode: "word" | "sentence" | "section" | null
): boolean => {
  if (!mode || highlightTexts.length === 0) return false;

  const normalizedText = text.trim().toLowerCase();

  if (mode === "word") {
    // Word-level: Check if text contains any of the highlight words
    return highlightTexts.some((word) => matchWordInText(normalizedText, word));
  } else if (mode === "sentence") {
    // Sentence-level: Check if text matches any of the sentences (fuzzy)
    return highlightTexts.some((sentence) => matchSentenceInText(normalizedText, sentence));
  }

  return false;
};

/**
 * Check if current rendering context matches the highlight target
 * This ensures highlights only apply to the correct section and item
 * 
 * @param currentSection - The section being rendered (e.g., 'experience')
 * @param currentItemIndex - The item index being rendered (e.g., 0, 1, 2)
 * @param highlightContext - The highlight context from useHighlight()
 * @returns true if this item should be checked for highlights
 */
export const isHighlightTarget = (
  currentSection: string,
  currentItemIndex: number | null,
  highlightContext: HighlightContextValue
): boolean => {
  if (!highlightContext.enabled) return false;
  if (!highlightContext.mode) return false;
  
  // Special case: 'overall' section highlights across ALL sections
  // Used for Overall section's repeated sentences/verbs that span the entire resume
  if (highlightContext.section === 'overall') return true;
  
  // Regular section-specific highlighting
  if (highlightContext.section !== currentSection) return false;
  
  // itemIndex can be null for section-wide highlights (like profile/personal section)
  // If both are null, it's a match. If highlightContext.itemIndex is null, match any item.
  // If highlightContext.itemIndex is a number, match only that specific item.
  if (highlightContext.itemIndex !== null && highlightContext.itemIndex !== currentItemIndex) {
    return false;
  }
  
  return true;
};

/**
 * Get merged highlight styles, preserving existing formatting
 * This allows combining bold/italic/underline with highlight styles
 * 
 * @param existingStyle - Existing inline styles from the element
 * @param highlightStyle - Highlight styles from context (mode-specific)
 * @param applyHighlight - Whether to apply highlight (based on text match)
 * @returns Merged style object
 */
export const getMergedHighlightStyle = (
  existingStyle: {
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    [key: string]: any;
  },
  highlightStyle: HighlightContextValue['style'],
  applyHighlight: boolean
): any => {
  if (!applyHighlight) {
    // Return existing styles only
    return {
      fontWeight: existingStyle.bold ? 'bold' : 'normal',
      fontStyle: existingStyle.italic ? 'italic' : 'normal',
      textDecoration: existingStyle.underline ? 'underline' : 'none',
    };
  }

  // Merge highlight with existing styles
  return {
    backgroundColor: highlightStyle.backgroundColor,
    color: highlightStyle.color || highlightStyle.textDecorationColor, // Use highlight color for text
    fontWeight: existingStyle.bold ? 'bold' : (highlightStyle.fontWeight || 'normal'),
    fontStyle: existingStyle.italic ? 'italic' : 'normal',
    textDecoration: combineTextDecorations(
      existingStyle.underline,
      highlightStyle.textDecoration
    ),
    textDecorationStyle: highlightStyle.textDecorationStyle,
    textDecorationColor: highlightStyle.textDecorationColor,
  };
};

/**
 * Combine multiple text decorations 
 */
const combineTextDecorations = (
  existingUnderline: boolean | undefined,
  highlightDecoration: string | undefined
): string => {
  if (existingUnderline && highlightDecoration) {
    // Both exist - combine them
    return `underline ${highlightDecoration}`;
  }
  if (existingUnderline) return 'underline';
  if (highlightDecoration) return highlightDecoration;
  return 'none';
};

/**
 * word matching with proper regex patterns
 */
const matchWordInText = (normalizedText: string, word: string): boolean => {
  const normalizedWord = normalize(word);
  if (!normalizedWord) return false;
  
  try {
    
    const escapedWord = escapeRegex(normalizedWord);
    
    // Use word boundary for English words, but fallback for special characters
    // \b doesn't work well with non-ASCII characters
    const hasSpecialChars = /[^\w\s]/.test(normalizedWord);
    
    if (hasSpecialChars) {
      // For special characters, use simpler matching
      const pattern = `(^|\\s|[^\\w])${escapedWord}($|\\s|[^\\w])`;
      const re = new RegExp(pattern, "i");
      return re.test(normalizedText);
    } else {
      // For regular words, use word boundaries
      const pattern = `\\b${escapedWord}\\b`;
      const re = new RegExp(pattern, "iu"); // 'u' flag for Unicode
      return re.test(normalizedText);
    }
  } catch (e) {
    console.warn("Regex error in matchWordInText:", e);
    // Fallback: simple includes
    return normalizedText.includes(normalizedWord);
  }
};

/**
 * Robust sentence matching with word-count based logic
 * 
 * Edge cases handled:
 * 1. "I" → should NOT match "I was promoted" (too short)
 * 2. "I was promoted" → SHOULD match "I was promoted to senior role" (substring)
 * 3. "I was promoted" → SHOULD match "I was promoted." (punctuation diff)
 * 4. Single-word vs multi-word distinction
 * 
 * Algorithm:
 * - Very short text (1 word, <4 chars): require exact match
 * - Actual sentences (2+ words OR 8+ chars): check if resume contains hovered text
 * - Medium single words (4-7 chars): use length ratio for fuzzy matching
 */
const matchSentenceInText = (resumeText: string, hoveredSentence: string): boolean => {
  const normalizedResume = normalize(resumeText);
  const normalizedHovered = normalize(hoveredSentence);
  
  // Quick check: exact match (case-insensitive)
  if (normalizedResume === normalizedHovered) {
    return true;
  }
  
  // Remove punctuation for flexible matching
  const stripPunct = (s: string) => s.replace(/[.,;:!?'"()[\]{}]/g, '');
  const resumeNoPunct = stripPunct(normalizedResume);
  const hoveredNoPunct = stripPunct(normalizedHovered);
  
  // Check: exact match without punctuation
  if (resumeNoPunct === hoveredNoPunct) {
    return true;
  }
  
  // Count words in hovered text to determine if it's a sentence or single word
  const wordCount = hoveredNoPunct.split(/\s+/).filter(Boolean).length;
  
  // CASE 1: Very short single word (e.g., "I", "a", "me")
  // These should only match exact occurrences, not be contained in longer sentences
  if (wordCount === 1 && hoveredNoPunct.length < 4) {
    // Already checked exact matches above, so return false
    return false;
  }
  
  // CASE 2: Actual sentences (multi-word OR long single word/phrase)
  // Check if resume bullet point CONTAINS the hovered sentence
  // This allows "I was promoted" to match "I was promoted to senior role"
  if (wordCount >= 2 || hoveredNoPunct.length >= 8) {
    return resumeNoPunct.includes(hoveredNoPunct);
  }
  
  // CASE 3: Medium-length single words (4-7 chars, e.g., "java", "code")
  // Use length-based fuzzy matching to handle slight variations
  const lengthRatio = Math.min(resumeNoPunct.length, hoveredNoPunct.length) / 
                      Math.max(resumeNoPunct.length, hoveredNoPunct.length);
  
  if (lengthRatio >= 0.7) {
    return resumeNoPunct.includes(hoveredNoPunct) || hoveredNoPunct.includes(resumeNoPunct);
  }
  
  return false;
};

// Helpers
const normalize = (s: string) => s.trim().toLowerCase().replace(/\s+/g, " ");

const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Check whether a given section/item should be highlighted (section-level highlighting)
 * Usage in templates: isSectionHighlighted('experience', idx)
 */
export const isSectionHighlighted = (
  sectionName: string,
  index: number,
  ctx?: HighlightContextValue
) => {
  // If ctx is provided, use it; otherwise read from hook
  if (ctx) {
    return ctx.enabled && ctx.mode === "section" && ctx.section === sectionName && ctx.itemIndex === index;
  }
  const h = ({} as any) as HighlightContextValue;
  // In template code prefer using useHighlight() and call isSectionHighlighted with that value
  return false;
};
